import { createFileRoute } from "@tanstack/react-router"
import {
  CATALOG_INDEX_STORE,
  marketplaceIdSchema,
  PRODUCT_CATALOG_OFFER_STATE,
  PRODUCT_CATALOG_MARKETPLACE_STATE,
  STORE_CODES,
  masterCategorySchema,
  productCatalogMarketplaceStateSchema,
  productCatalogOfferStateSchema,
  productFlagFilterSchema,
  PRODUCTS_LIST_MAX_QUERY_LENGTH,
  type MasterCategory,
  type ProductCatalogMarketplaceState,
  type ProductCatalogOfferState,
  type ProductFlagFilter,
  type ProductsListResponse,
} from "@nova-ai/shared"
import { Boxes, Check } from "lucide-react"
import { useEffect, useRef } from "react"
import { z } from "zod"

import { AppShell } from "../components/layout/app-shell"
import { ProductMarketplaceSelect } from "../components/products/product-marketplace-select"
import { ProductsGrid } from "../components/products/products-grid"
import { ProductStoreSelect } from "../components/products/product-store-select"
import { SearchInput } from "../components/ui/search-input"
import { SegmentedControl, type SegmentedOption } from "../components/ui/segmented-control"
import { StateToggle, type StateToggleOption } from "../components/ui/state-toggle"
import { useDebounce } from "../hooks/use-debounce"
import { humanizeEnumLabel } from "../lib/enum-label"
import { formatDateTime, formatRelativeTime } from "../lib/format-date"
import { cn } from "../lib/utils"
import { useMarketplaces } from "../services/use-dashboard"
import { useProducts } from "../services/use-products"

// Every axis carries `.catch()` (issue #318). A search param is a string an
// operator can hand-edit, so a bad value must degrade its own filter to that
// axis's default and leave the rest of the page working — never throw out of
// `validateSearch`, which takes the whole route to the error boundary. `?live=yes`
// is the API's own spelling for this axis, so it was an easy URL to guess and
// crash. Keep `.catch()` on every key added here: `-products.index.test.tsx`
// pins a malformed value per axis and fails on a key that has no case.
export const productsSearchSchema = z.object({
  store: z.enum(STORE_CODES).optional().catch(CATALOG_INDEX_STORE),
  category: masterCategorySchema.optional().catch(undefined),
  // `q` is the axis where recovering to the default is the wrong recovery: the
  // default is "no search", so a discarded query silently shows the operator a
  // different result set than the URL they were sent promises. Two ways it
  // arrives non-string, both from the URL an operator hand-edits or pastes:
  // TanStack parses search with `JSON.parse`, so `?q=12345` is the NUMBER 12345
  // — and config SKUs are numeric (`100`, `200`) in the very field whose
  // placeholder says "config SKU" — while `?q=<101 chars>` is past the cap. So
  // coerce to text and truncate, exactly what the input itself does on typing
  // (`value.slice` below); the URL path and the keyboard path must not disagree.
  // `.catch()` stays as the class guard even though nothing reaches it today.
  q: z.coerce
    .string()
    .transform((value) => value.slice(0, PRODUCTS_LIST_MAX_QUERY_LENGTH))
    .optional()
    .catch(undefined),
  ownBrand: productFlagFilterSchema.default("any").catch("any"),
  live: z.boolean().default(true).catch(true),
  marketplace: marketplaceIdSchema.optional().catch(undefined),
  marketplaceState: productCatalogMarketplaceStateSchema.optional().catch(undefined),
  offerState: productCatalogOfferStateSchema.optional().catch(undefined),
})

const BRAND_OPTIONS: readonly SegmentedOption<ProductFlagFilter>[] = [
  { value: "any", label: "Any" },
  { value: "yes", label: "Own brand" },
  { value: "no", label: "Third-party" },
]

type ProductsSearch = z.infer<typeof productsSearchSchema>

function selectedMarketplaceState(search: ProductsSearch) {
  return search.marketplace === undefined
    ? undefined
    : (search.marketplaceState ?? PRODUCT_CATALOG_MARKETPLACE_STATE.all)
}

function selectedOfferState(search: ProductsSearch) {
  return search.marketplace === undefined
    ? undefined
    : (search.offerState ?? PRODUCT_CATALOG_OFFER_STATE.all)
}

function buildProductsFilter(
  search: ProductsSearch,
  store: (typeof STORE_CODES)[number],
  debouncedQuery: string,
  marketplaceState: ProductCatalogMarketplaceState | undefined,
  offerState: ProductCatalogOfferState | undefined
) {
  return {
    ...(search.marketplace === undefined ? { store } : {}),
    ...(search.category !== undefined ? { category: search.category } : {}),
    ...(debouncedQuery.length > 0 ? { query: debouncedQuery } : {}),
    ownBrand: search.ownBrand,
    live: search.live ? ("yes" as const) : ("any" as const),
    ...(search.marketplace !== undefined ? { marketplace: search.marketplace } : {}),
    ...(marketplaceState !== undefined ? { marketplaceState } : {}),
    ...(offerState !== undefined ? { offerState } : {}),
  }
}

function useMarketplaceStoreBinding(
  search: ProductsSearch,
  explicitStore: (typeof STORE_CODES)[number]
) {
  const navigate = Route.useNavigate()
  const lastExplicitStore = useRef(explicitStore)

  useEffect(() => {
    if (search.marketplace === undefined) {
      lastExplicitStore.current = explicitStore
    }
    if (
      search.marketplace === undefined &&
      (search.marketplaceState !== undefined || search.offerState !== undefined)
    ) {
      void navigate({
        search: (previous) => ({
          ...previous,
          marketplaceState: undefined,
          offerState: undefined,
        }),
        replace: true,
      })
    }
    if (search.marketplace !== undefined && search.store !== undefined) {
      lastExplicitStore.current = search.store
      void navigate({
        search: (previous) => ({ ...previous, store: undefined }),
        replace: true,
      })
    }
  }, [explicitStore, navigate, search])

  return lastExplicitStore
}

function useUnavailableOfferFilterReset(
  search: ProductsSearch,
  offerSnapshot: ProductsListResponse["offerSnapshot"]
) {
  const navigate = Route.useNavigate()

  useEffect(() => {
    if (
      search.marketplace !== undefined &&
      offerSnapshot !== undefined &&
      offerSnapshot?.activityAvailable !== true &&
      search.offerState !== undefined
    ) {
      void navigate({
        search: (previous) => ({ ...previous, offerState: undefined }),
        replace: true,
      })
    }
  }, [navigate, offerSnapshot, search.marketplace, search.offerState])
}

type SetProductsSearch = (next: Partial<ProductsSearch>) => void

function ProductsFilters({
  search,
  explicitStore,
  resolvedStore,
  lastExplicitStore,
  marketplaceState,
  offerState,
  marketplaceStateCounts,
  offerStateCounts,
  offerSnapshot,
  setSearch,
}: {
  search: ProductsSearch
  explicitStore: (typeof STORE_CODES)[number]
  resolvedStore: (typeof STORE_CODES)[number] | undefined
  lastExplicitStore: { current: (typeof STORE_CODES)[number] }
  marketplaceState: ProductCatalogMarketplaceState | undefined
  offerState: ProductCatalogOfferState | undefined
  marketplaceStateCounts: ProductsListResponse["marketplaceStateCounts"]
  offerStateCounts: ProductsListResponse["offerStateCounts"]
  offerSnapshot: ProductsListResponse["offerSnapshot"]
  setSearch: SetProductsSearch
}) {
  const navigate = Route.useNavigate()
  const marketplaces = useMarketplaces()
  const query = search.q ?? ""

  return (
    <div className="mb-6 flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CategoryFilters value={search.category} onChange={(category) => setSearch({ category })} />

        <div className="flex w-full items-center sm:w-auto">
          <SearchInput
            value={query}
            onChange={(value) =>
              setSearch({
                q: value.length > 0 ? value.slice(0, PRODUCTS_LIST_MAX_QUERY_LENGTH) : undefined,
              })
            }
            placeholder="Search name, config SKU, or variant SKU…"
            ariaLabel="Search products"
            className="min-w-0 flex-1 sm:w-96"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <div className="flex min-w-40 flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Store</span>

          {search.marketplace === undefined ? (
            <ProductStoreSelect
              id="products-store"
              value={explicitStore}
              onChange={(nextStore) => {
                lastExplicitStore.current = nextStore
                void navigate({ search: (previous) => ({ ...previous, store: nextStore }) })
              }}
            />
          ) : (
            <span className="inline-flex min-h-9 items-center rounded-lg border border-border bg-muted/50 px-3 text-[13px] font-medium text-foreground">
              {resolvedStore ?? "Resolving…"}
            </span>
          )}
        </div>

        <label htmlFor="products-marketplace" className="flex min-w-48 flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Marketplace</span>

          <ProductMarketplaceSelect
            id="products-marketplace"
            marketplaces={marketplaces.data ?? []}
            value={search.marketplace}
            isLoading={marketplaces.isLoading}
            isError={marketplaces.isError}
            onChange={(marketplace) =>
              setSearch({
                marketplace,
                store: marketplace === undefined ? lastExplicitStore.current : undefined,
                marketplaceState: undefined,
                offerState: undefined,
              })
            }
          />
        </label>

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Brand</span>

          <SegmentedControl
            options={BRAND_OPTIONS}
            value={search.ownBrand}
            onChange={(ownBrand) => setSearch({ ownBrand })}
            label="Brand"
          />
        </div>

        <CatalogLiveToggle value={search.live} onChange={(live) => setSearch({ live })} />

        {search.marketplace !== undefined && marketplaceState !== undefined ? (
          <div className="ml-auto">
            <StateToggle
              label="Marketplace state"
              value={marketplaceState}
              options={stateOptions(marketplaceStateCounts)}
              onChange={(nextState) =>
                setSearch({
                  marketplaceState:
                    nextState === PRODUCT_CATALOG_MARKETPLACE_STATE.all ? undefined : nextState,
                })
              }
            />
          </div>
        ) : null}

        {search.marketplace !== undefined &&
        offerState !== undefined &&
        offerSnapshot?.activityAvailable === true ? (
          <StateToggle
            label="Offer state"
            value={offerState}
            options={offerStateOptions(offerStateCounts)}
            onChange={(nextState) =>
              setSearch({
                offerState: nextState === PRODUCT_CATALOG_OFFER_STATE.all ? undefined : nextState,
              })
            }
          />
        ) : null}
      </div>

      {search.marketplace !== undefined ? <OfferSnapshotStatus snapshot={offerSnapshot} /> : null}
    </div>
  )
}

function ProductsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const query = search.q ?? ""
  const explicitStore = search.store ?? CATALOG_INDEX_STORE
  const lastExplicitStore = useMarketplaceStoreBinding(search, explicitStore)
  const debouncedQuery = useDebounce(query.trim(), 300)
  const marketplaceState = selectedMarketplaceState(search)
  const offerState = selectedOfferState(search)

  const productsQuery = useProducts(
    buildProductsFilter(search, explicitStore, debouncedQuery, marketplaceState, offerState)
  )
  const products = productsQuery.data?.pages.flatMap((page) => page.products) ?? []
  const marketplaceStateCounts = productsQuery.data?.pages[0]?.marketplaceStateCounts
  const offerStateCounts = productsQuery.data?.pages[0]?.offerStateCounts
  const offerSnapshot = productsQuery.data?.pages[0]?.offerSnapshot
  const resolvedStore = productsQuery.data?.pages[0]?.store
  useUnavailableOfferFilterReset(search, offerSnapshot)

  const setSearch = (next: Partial<z.infer<typeof productsSearchSchema>>) => {
    void navigate({ search: (previous) => ({ ...previous, ...next }), replace: true })
  }

  const clearFilters = () => {
    void navigate({
      search: {
        store: lastExplicitStore.current,
        ownBrand: "any",
        live: true,
        marketplace: undefined,
        marketplaceState: undefined,
        offerState: undefined,
      },
      replace: true,
    })
  }

  return (
    <AppShell
      title="Products"
      icon={
        <span className="inline-flex size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Boxes className="size-4" />
        </span>
      }
    >
      <ProductsFilters
        search={search}
        explicitStore={explicitStore}
        resolvedStore={resolvedStore}
        lastExplicitStore={lastExplicitStore}
        marketplaceState={marketplaceState}
        offerState={offerState}
        marketplaceStateCounts={marketplaceStateCounts}
        offerStateCounts={offerStateCounts}
        offerSnapshot={offerSnapshot}
        setSearch={setSearch}
      />

      <ProductsGrid
        products={products}
        isLoading={productsQuery.isLoading}
        error={productsQuery.error}
        hasNextPage={productsQuery.hasNextPage}
        isFetchingNextPage={productsQuery.isFetchingNextPage}
        onLoadMore={() => productsQuery.fetchNextPage()}
        onClearFilters={clearFilters}
        emptyMessage={emptyMessage(search.marketplace, marketplaceState, offerState)}
      />
    </AppShell>
  )
}

function stateOptions(
  counts: { all: number; ok: number; nok: number; pending: number } | undefined
): StateToggleOption<ProductCatalogMarketplaceState>[] {
  return [
    {
      value: PRODUCT_CATALOG_MARKETPLACE_STATE.all,
      label: "All",
      ...(counts === undefined ? {} : { count: counts.all }),
    },
    {
      value: "ok",
      label: "OK",
      ...(counts === undefined ? {} : { count: counts.ok }),
    },
    {
      value: "nok",
      label: "NOK",
      tone: "warning",
      ...(counts === undefined ? {} : { count: counts.nok }),
      countTitle: `${counts?.nok ?? 0} products with a NOK marketplace verdict`,
    },
    {
      value: "pending",
      label: "Pending",
      ...(counts === undefined ? {} : { count: counts.pending }),
    },
  ]
}

function offerStateOptions(
  counts:
    | { all: number; active: number; partial: number; inactive: number; missing: number }
    | undefined
): StateToggleOption<ProductCatalogOfferState>[] {
  return [
    {
      value: PRODUCT_CATALOG_OFFER_STATE.all,
      label: "All",
      ...(counts === undefined ? {} : { count: counts.all }),
    },
    {
      value: PRODUCT_CATALOG_OFFER_STATE.active,
      label: "Active",
      ...(counts === undefined ? {} : { count: counts.active }),
    },
    {
      value: PRODUCT_CATALOG_OFFER_STATE.partial,
      label: "Partial",
      tone: "warning",
      ...(counts === undefined ? {} : { count: counts.partial }),
    },
    {
      value: PRODUCT_CATALOG_OFFER_STATE.inactive,
      label: "Inactive",
      tone: "warning",
      ...(counts === undefined ? {} : { count: counts.inactive }),
    },
    {
      value: PRODUCT_CATALOG_OFFER_STATE.missing,
      label: "Missing",
      ...(counts === undefined ? {} : { count: counts.missing }),
    },
  ]
}

function OfferSnapshotStatus({ snapshot }: { snapshot: ProductsListResponse["offerSnapshot"] }) {
  if (snapshot === undefined || snapshot === null) {
    return (
      <p className="text-xs text-muted-foreground">
        Offer activity Unknown · No stored offer snapshot
      </p>
    )
  }
  const captured = `Captured ${formatRelativeTime(snapshot.capturedAt)}`
  const title = formatDateTime(snapshot.capturedAt)
  if (!snapshot.activityAvailable) {
    return (
      <p className="text-xs text-warning" title={title}>
        Offer activity Unknown · {captured} · Run offer sync to activate activity filtering
      </p>
    )
  }
  return (
    <p
      className={cn(
        "text-xs",
        snapshot.freshness === "stale" ? "font-medium text-warning" : "text-muted-foreground"
      )}
      title={title}
    >
      <span>{`${snapshot.freshness === "stale" ? "Stale offer snapshot" : "Fresh offer snapshot"} · ${captured}`}</span>
    </p>
  )
}

function emptyMessage(
  marketplace: string | undefined,
  marketplaceState: ProductCatalogMarketplaceState | undefined,
  offerState: ProductCatalogOfferState | undefined
): string {
  if (marketplace === undefined) {
    return "No products match these store filters."
  }
  if (offerState !== undefined && offerState !== PRODUCT_CATALOG_OFFER_STATE.all) {
    const verdict =
      marketplaceState !== undefined && marketplaceState !== PRODUCT_CATALOG_MARKETPLACE_STATE.all
        ? `${marketplaceState.toUpperCase()} products`
        : "products"
    return `No ${verdict} with ${offerState} offer activity match these filters.`
  }
  if (
    marketplaceState === undefined ||
    marketplaceState === PRODUCT_CATALOG_MARKETPLACE_STATE.all
  ) {
    return "No products from this store belong to the selected marketplace."
  }
  return `No ${marketplaceState.toUpperCase()} products match these filters.`
}

// This is the render pipeline's `product_not_live` predicate. A delisted variant
// remains live while raw inventory is explicitly in stock; false or unknown raw
// inventory excludes it. A listed product can still be live without stock.
function CatalogLiveToggle({
  value,
  onChange,
}: {
  value: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <button
      type="button"
      aria-pressed={value}
      onClick={() => onChange(!value)}
      title="Hide products that are disabled, locally banned, long-term unavailable, or delisted without raw inventory"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
        value
          ? "border-border bg-card text-foreground shadow-card"
          : "border-transparent text-muted-foreground hover:bg-foreground/5"
      )}
    >
      <Check className={cn("size-3.5", !value && "opacity-30")} aria-hidden="true" />
      Catalog live
    </button>
  )
}

function CategoryFilters({
  value,
  onChange,
}: {
  value: MasterCategory | undefined
  onChange: (category: MasterCategory | undefined) => void
}) {
  const options: { value: MasterCategory | undefined; label: string }[] = [
    { value: undefined, label: "All" },
    ...masterCategorySchema.options.map((category) => ({
      value: category,
      label: humanizeEnumLabel(category),
    })),
  ]

  return (
    <div
      role="group"
      aria-label="Product category"
      className="inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-full bg-muted p-0.5 text-[13px] sm:w-fit"
    >
      {options.map((option) => {
        const isActive = option.value === value
        return (
          <button
            key={option.label}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option.value)}
            className={cn(
              "shrink-0 rounded-full px-3 py-1.5 font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
              isActive
                ? "bg-card text-foreground shadow-card ring-1 ring-border"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export const Route = createFileRoute("/products/")({
  validateSearch: productsSearchSchema,
  component: ProductsPage,
})
